import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";
import { ColDef } from "ag-grid-community";
import images from "../../../assets/images";
import {
    getAllBCPPlans,
    deleteBCPPlan,
    activateBCPPlan,
    deactivateBCPPlan,
    getUserDirectory,
} from "../../../service/api";
import { toast } from "react-toastify";
import { useRoleManageStore } from "../../../store/store";

interface BCPPlan {
    _id: string;
    planName: string;
    description: string;
    criticalFunctions: Array<{
        functionName: string;
        priority: string;
        description: string;
    }>;
    recoveryObjectives: {
        rto: number;
        rpo: number;
    };
    alternateSites: Array<{
        siteName: string;
        siteType: string;
        address: string;
        capacity: number;
    }>;
    itSystems: Array<{
        systemName: string;
        criticality: string;
        backupLocation: string;
        recoveryProcedure: string;
    }>;
    assignedUserId?: string;
    assignedUserName?: string;
    assignedRoleId: string;
    assignedRoleName?: string;
    linkedIncidentId?: string;
    status: "Draft" | "Active" | "Activated" | "Completed" | "Archived";
    activationDate?: string;
    completionDate?: string;
    createdAt?: string;
    updatedAt?: string;
}

const gridTheme = themeQuartz.withParams({
    backgroundColor: "#fff",
    foregroundColor: "#2a2a2a",
    headerTextColor: "#2a2a2a",
    headerBackgroundColor: "#E9F5FF",
    oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
    headerColumnResizeHandleColor: "rgb(126, 46, 132)",
    fontSize: "0.75rem",
    headerFontWeight: "bold",
});

const BCPPlans: React.FC = () => {
    const navigate = useNavigate();
    const [rowData, setRowData] = useState<BCPPlan[]>([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string>("All");
    const gridRef = useRef<AgGridReact>(null);
    const { rolesData, fetchAllRoles } = useRoleManageStore();
    const [usersData, setUsersData] = useState<any[]>([]);

    useEffect(() => {
        fetchAllRoles();
        fetchUsersData();
        fetchPlans();
    }, []);

    const fetchUsersData = async () => {
        try {
            const response = await getUserDirectory();
            const users = Array.isArray(response.data.data) ? response.data.data : [];
            setUsersData(users);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const params = statusFilter !== "All" ? { status: statusFilter } : {};
            const response = await getAllBCPPlans(params);
            if (response.data?.resCode === 200 && response.data?.data) {
                const plans = Array.isArray(response.data.data)
                    ? response.data.data
                    : [];

                // Map role names and user names to plans
                const plansWithNames = plans.map((plan: BCPPlan) => {
                    const role = rolesData.find((r) => r._id === plan.assignedRoleId);
                    const user = usersData.find((u) => u._id === plan.assignedUserId);
                    const userName = user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "Unknown";

                    return {
                        ...plan,
                        assignedRoleName: role?.roleName || "N/A",
                        assignedUserName: userName || "N/A",
                    };
                });

                setRowData(plansWithNames);
            }
        } catch (error: any) {
            console.error("Error fetching Business Continuity Plans:", error);
            toast.error(
                error.response?.data?.resMessage ||
                "Failed to fetch Business Continuity Plans"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Refresh plans when roles or users are loaded
        if ((rolesData.length > 0 || usersData.length > 0) && rowData.length > 0) {
            const plansWithNames = rowData.map((plan) => {
                const role = rolesData.find((r) => r._id === plan.assignedRoleId);
                const user = usersData.find((u) => u._id === plan.assignedUserId);
                const userName = user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "Unknown";

                return {
                    ...plan,
                    assignedRoleName: role?.roleName || "N/A",
                    assignedUserName: userName || "N/A",
                };
            });
            setRowData(plansWithNames);
        }
    }, [rolesData.length, usersData.length]);

    useEffect(() => {
        fetchPlans();
    }, [statusFilter]);

    const handleAddBCPPlan = () => {
        navigate("/external-emergency-preparedness/add-bcp-plan");
    };

    const handleView = (plan: BCPPlan) => {
        navigate(
            `/external-emergency-preparedness/add-bcp-plan?id=${plan._id}&mode=view`
        );
    };

    const handleEdit = (plan: BCPPlan) => {
        navigate(
            `/external-emergency-preparedness/add-bcp-plan?id=${plan._id}&mode=edit`
        );
    };

    const handleDelete = async (id: string) => {
        const confirmed = window.confirm(
            "Are you sure you want to delete this Business Continuity Plan?"
        );
        if (confirmed) {
            try {
                const response = await deleteBCPPlan(id);
                if (response.data?.resCode === 200) {
                    toast.success(
                        response.data?.resMessage || "Business Continuity Plan deleted successfully"
                    );
                    fetchPlans();
                } else {
                    throw new Error(
                        response.data?.resMessage || "Failed to delete Business Continuity Plan"
                    );
                }
            } catch (error: any) {
                console.error("Error deleting Business Continuity Plan:", error);
                toast.error(
                    error.response?.data?.resMessage ||
                    error.message ||
                    "Failed to delete Business Continuity Plan"
                );
            }
        }
    };

    const handleActivate = async (plan: BCPPlan) => {
        const confirmed = window.confirm(
            `Are you sure you want to activate the BCP Plan: ${plan.planName}?`
        );
        if (confirmed) {
            try {
                const response = await activateBCPPlan(plan._id, {});
                if (response.data?.resCode === 200) {
                    toast.success("Business Continuity Plan activated successfully");
                    fetchPlans();
                } else {
                    throw new Error("Failed to activate Business Continuity Plan");
                }
            } catch (error: any) {
                console.error("Error activating Business Continuity Plan:", error);
                toast.error(
                    error.response?.data?.resMessage ||
                    "Failed to activate Business Continuity Plan"
                );
            }
        }
    };

    const handleDeactivate = async (plan: BCPPlan) => {
        const confirmed = window.confirm(
            `Are you sure you want to deactivate the BCP Plan: ${plan.planName}?`
        );
        if (confirmed) {
            try {
                const response = await deactivateBCPPlan(plan._id);
                if (response.data?.resCode === 200) {
                    toast.success("Business Continuity Plan deactivated successfully");
                    fetchPlans();
                } else {
                    throw new Error("Failed to deactivate Business Continuity Plan");
                }
            } catch (error: any) {
                console.error("Error deactivating Business Continuity Plan:", error);
                toast.error(
                    error.response?.data?.resMessage ||
                    "Failed to deactivate Business Continuity Plan"
                );
            }
        }
    };

    const getStatusBadge = (status: string) => {
        const statusColors: Record<string, string> = {
            Draft: "bg-gray-100 text-gray-800",
            Active: "bg-blue-100 text-blue-800",
            Activated: "bg-green-100 text-green-800",
            Completed: "bg-purple-100 text-purple-800",
            Archived: "bg-red-100 text-red-800",
        };

        return (
            <span
                className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[status] || "bg-gray-100 text-gray-800"
                    }`}
            >
                {status}
            </span>
        );
    };

    const columnDefs = useMemo<ColDef<BCPPlan>[]>(
        () => [
            {
                field: "planName",
                headerName: "Plan Name",
                minWidth: 200,
                flex: 1.5,
                headerClass: "bg-[#e9f5ff]",
                cellStyle: { padding: "10px 10px 0 10px" },
            },
            {
                field: "status",
                headerName: "Status",
                minWidth: 120,
                flex: 0.8,
                headerClass: "bg-[#e9f5ff]",
                cellStyle: { padding: "10px 10px 0 10px" },
                cellRenderer: (params: any) => {
                    return getStatusBadge(params.value);
                },
            },
            {
                headerName: "Critical Functions",
                minWidth: 150,
                flex: 1,
                headerClass: "bg-[#e9f5ff]",
                cellStyle: { padding: "10px 10px 0 10px" },
                valueGetter: (params: any) => {
                    return params.data?.criticalFunctions?.length || 0;
                },
            },
            {
                headerName: "RTO / RPO (Hours)",
                minWidth: 150,
                flex: 1,
                headerClass: "bg-[#e9f5ff]",
                cellStyle: { padding: "10px 10px 0 10px" },
                valueGetter: (params: any) => {
                    const rto = params.data?.recoveryObjectives?.rto || 0;
                    const rpo = params.data?.recoveryObjectives?.rpo || 0;
                    return `${rto} / ${rpo}`;
                },
            },
            {
                headerName: "Assigned Person",
                minWidth: 200,
                flex: 1.5,
                headerClass: "bg-[#e9f5ff]",
                cellStyle: { padding: "10px 10px 0 10px" },
                valueGetter: (params: any) => {
                    const userName = params.data?.assignedUserName || "Unknown";
                    const roleName = params.data?.assignedRoleName || "N/A";
                    return `${userName} (${roleName})`;
                },
            },
            {
                headerName: "Actions",
                flex: 1,
                headerClass: "bg-[#e9f5ff]",
                cellStyle: { padding: "10px 10px 0 10px" },
                cellRenderer: (params: any) => {
                    const plan = params.data as BCPPlan;
                    return (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleView(plan)}
                                title="View BCP Plan"
                            >
                                <img
                                    className="iconTd"
                                    src={images.viewIcon}
                                    alt="view"
                                />
                            </button>
                            <button
                                onClick={() => handleEdit(plan)}
                                title="Edit BCP Plan"
                            >
                                <img
                                    className="iconTd"
                                    src={images.editIcon}
                                    alt="edit"
                                />
                            </button>
                            <button
                                onClick={() => navigate(`/external-emergency-preparedness/bcp-plans/${plan._id}/recovery-tasks`)}
                                className="hover:opacity-70"
                                title="Recovery Tasks"
                            >
                                <svg
                                    className="w-5 h-5 text-blue-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                                    />
                                </svg>
                            </button>
                            {plan.status === "Draft" || plan.status === "Active" ? (
                                <button
                                    onClick={() => handleActivate(plan)}
                                    className="hover:opacity-70"
                                    title="Activate BCP Plan"
                                >
                                    <svg
                                        className="w-5 h-5 text-green-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                </button>
                            ) : null}
                            {plan.status === "Activated" ? (
                                <button
                                    onClick={() => handleDeactivate(plan)}
                                    className="hover:opacity-70"
                                    title="Deactivate BCP Plan"
                                >
                                    <svg
                                        className="w-5 h-5 text-orange-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </button>
                            ) : null}
                            <button
                                onClick={() => handleDelete(plan._id)}
                                title="Delete BCP Plan"
                                aria-label="Delete BCP Plan"
                            >
                                <Trash2 className="text-red-500" size={20} />
                            </button>
                        </div>
                    );
                },
                filter: false,
                sortable: false,
            },
        ],
        [rolesData]
    );

    const defaultColDef = useMemo(() => {
        return {
            editable: false,
            filter: false,
            tooltipComponentParams: {
                color: "#ececec",
                bgColor: "#1f2937",
                textColor: "#fff",
            },
        };
    }, []);

    const theme = useMemo(() => gridTheme, []);

    useEffect(() => {
        // Auto-size columns on mount
        setTimeout(() => {
            gridRef.current?.api.sizeColumnsToFit();
        }, 0);
    }, [rowData]);

    return (
        <div className="px-2 py-4 mt-14 w-full h-full">
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-semibold text-gray-900">
                    Business Continuity Plans
                </h1>
                <div className="flex gap-3 items-center">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                        <option value="All">All Status</option>
                        <option value="Draft">Draft</option>
                        <option value="Active">Active</option>
                        <option value="Activated">Activated</option>
                        <option value="Completed">Completed</option>
                        <option value="Archived">Archived</option>
                    </select>
                    <button
                        className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full hover:bg-teal-600 transition-colors"
                        onClick={handleAddBCPPlan}
                    >
                        <Plus size={20} />
                        Add BCP Plan
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-4">
                {rowData.length > 0 || loading ? (
                    <div className="ag-theme-quartz w-full min-h-[400px]">
                        <AgGridReact
                            ref={gridRef}
                            rowData={rowData}
                            columnDefs={columnDefs}
                            theme={theme}
                            headerHeight={65}
                            rowHeight={50}
                            loading={loading}
                            defaultColDef={defaultColDef}
                            domLayout="autoHeight"
                            pagination={true}
                            paginationPageSize={10}
                            animateRows={true}
                        />
                    </div>
                ) : (
                    <div className="flex flex-col justify-center items-center h-64">
                        <p className="text-gray-500 text-lg mb-4">
                            {loading
                                ? "Loading Business Continuity Plans..."
                                : "No Business Continuity Plans found. Click 'Add BCP Plan' to create one."}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BCPPlans;
